if (!customElements.get('wlsn-buybox-sticky')) {
  class WLSNStickyBuybox extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        loading: 'loading',
        buybox: 'js-buybox',
        footer: 'js-footer',
      };

      // Elements that hide the sticky when visible
      this.hideConditions = [
        { selector: `.${this.classes.buybox}`, description: 'Buybox', hideUntilPassed: true },
        { selector: `.${this.classes.footer}`, description: 'Footer' },
      ];

      this.observers = {};
      this.visibilityStates = {};
      this.elementsCache = null;
      this._stickyData = null;
      this._initialized = false;
      this._ownershipObserver = null;

      this.boundHandleResize = this._handleResize.bind(this);
      this.boundHandleVariantChange = this._handleVariantChange.bind(this);
      this.boundHandlePurchaseTypeChange = this._handlePurchaseTypeChange.bind(this);
      this.boundHandleModify = this._handleModify.bind(this);
      this.boundHandleAtc = this._handleAtc.bind(this);
    }

    /**
     * Lazily resolves and caches all DOM references needed by this element.
     * Re-uses the cache on subsequent calls; invalidated by _cleanup().
     * @returns {{ buybox: Element|null, form: Element|null, variantInput: HTMLInputElement|null, purchaseTypeInput: HTMLInputElement|null, atcSubmitBtn: HTMLButtonElement|null, frequencySelector: Element|null, modifyBtn: HTMLButtonElement|null, atcWrapper: Element|null, atcBtn: HTMLButtonElement|null }}
     */
    get elements() {
      if (!this.elementsCache) {
        const buybox = this.closest(`.${this.classes.buybox}`);
        const form = buybox?.querySelector('[data-type="add-to-cart-form"]');
        this.elementsCache = {
          buybox,
          form,
          variantInput: form?.querySelector('input[name="id"]'),
          purchaseTypeInput: buybox?.querySelector('input[name="purchase-type"]'),
          atcSubmitBtn: buybox?.querySelector('[data-type="add-to-cart-form"] button[type="submit"]'),
          frequencySelector: buybox?.querySelector('wlsn-buybox-frequency-selector'),
          modifyBtn: this.querySelector('.js-sticky-modify'),
          atcWrapper: this.querySelector('.js-sticky-atc'),
          atcBtn: this.querySelector('.js-sticky-atc button'),
        };
      }
      return this.elementsCache;
    }

    /**
     * Connected callback — defers init by one tick so sibling custom elements
     * (wlsn-product-form, wlsn-buybox-frequency-selector) have connected first.
     * @returns {void}
     */
    connectedCallback() {
      this._ownershipObserver = new MutationObserver(() => this._refreshOwnership());
      this._ownershipObserver.observe(document.body, { childList: true, subtree: true });

      setTimeout(() => {
        this._refreshOwnership();
      }, 0);
    }

    /**
     * Disconnected callback — tears down observers and event listeners.
     * @returns {void}
     */
    disconnectedCallback() {
      this._ownershipObserver?.disconnect();
      this._ownershipObserver = null;

      if (this._initialized) {
        this._cleanup();
        this._initialized = false;
      }
    }

    /**
     * Gives the standard buybox sticky priority. The promo sticky is a fallback
     * and initializes only when no standard buybox sticky exists on the page.
     * Re-evaluated on section add/remove so Theme Editor changes work live.
     * @returns {void}
     */
    _refreshOwnership() {
      const buybox = this.closest(`.${this.classes.buybox}`);
      const isPromoSticky = buybox?.classList.contains('wlsn-buybox--promo');
      const standardSticky = document.querySelector(
        '.js-buybox:not(.wlsn-buybox--promo) wlsn-buybox-sticky'
      );
      const shouldYield = isPromoSticky && standardSticky;

      this.classList.toggle('wlsn-buybox-sticky--disabled', Boolean(shouldYield));

      if (shouldYield) {
        if (this._initialized) {
          this._cleanup();
          this._initialized = false;
        }
        return;
      }

      if (!this._initialized) {
        this._init();
        this._initialized = true;
      }
    }

    /**
     * Bootstraps all functionality: parses data, sets up observers,
     * attaches event listeners, and syncs the initial display state.
     * Aborts early if the parent buybox element cannot be found.
     * @returns {void}
     */
    _init() {
      if (!this.elements.buybox) {
        console.warn('[WLSNStickyBuybox] Missing buybox element');
        return;
      }

      this._loadStickyData();
      this._setupHideConditions();
      this._setupIntersectionObservers();
      this._setupEventListeners();
      this._syncFromCurrentState();

      window.addEventListener('resize', this.boundHandleResize, { passive: true });
    }

    /* ── Data ── */

    /**
     * Parses the embedded JSON price data from the `.js-sticky-data` script tag
     * and stores it on `this._stickyData` for use in `_syncDisplay`.
     * @returns {void}
     */
    _loadStickyData() {
      try {
        const script = this.querySelector('.js-sticky-data');
        if (script) this._stickyData = JSON.parse(script.textContent);
      } catch (e) {
        console.warn('[WLSNStickyBuybox] Failed to parse sticky data', e);
      }
    }

    /* ── Event listeners ── */

    /**
     * Attaches all DOM event listeners for variant changes, purchase-type changes,
     * the MODIFY button, and the sticky ATC button.
     * @returns {void}
     */
    _setupEventListeners() {
      const { variantInput, purchaseTypeInput, modifyBtn, atcBtn } = this.elements;
      if (variantInput) variantInput.addEventListener('change', this.boundHandleVariantChange);
      if (purchaseTypeInput) purchaseTypeInput.addEventListener('change', this.boundHandlePurchaseTypeChange);
      if (modifyBtn) modifyBtn.addEventListener('click', this.boundHandleModify);
      if (atcBtn) atcBtn.addEventListener('click', this.boundHandleAtc);
    }

    /**
     * Fired when `input[name="id"]` changes (quantity pill selection).
     * Re-syncs prices and meta text to match the new variant and current purchase type.
     * @returns {void}
     */
    _handleVariantChange() {
      const { variantInput, purchaseTypeInput } = this.elements;
      this._syncDisplay(variantInput?.value, purchaseTypeInput?.value || 'subscription');
    }

    /**
     * Fired when `input[name="purchase-type"]` changes (subscribe ↔ one-time).
     * Re-syncs prices and meta text to match the current variant and new purchase type.
     * @returns {void}
     */
    _handlePurchaseTypeChange() {
      const { variantInput, purchaseTypeInput } = this.elements;
      this._syncDisplay(variantInput?.value, purchaseTypeInput?.value || 'subscription');
    }

    /**
     * Fired when the MODIFY button is clicked (mobile only).
     * Smoothly scrolls the frequency selector into view so the user can change their selection.
     * Looks up the frequency selector fresh each call to avoid acting on a stale cache
     * reference after section re-renders.
     * @returns {void}
     */
    _handleModify() {
      const { buybox } = this.elements;
      const frequencySelector = buybox?.querySelector('wlsn-buybox-frequency-selector');
      if (frequencySelector) {
        frequencySelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    /**
     * Fired when the sticky ATC button is clicked.
     * Adds the loading class to the sticky button, then programmatically clicks
     * the main form submit button so the existing wlsn-product-form.js flow handles
     * the cart request. A MutationObserver mirrors the loading state removal back
     * to the sticky button once the form finishes.
     * @returns {void}
     */
    _handleAtc() {
      const { atcBtn, atcSubmitBtn } = this.elements;

      if (!atcSubmitBtn) return;

      // Mirror loading class on sticky button until the form clears it from the main button
      if (atcBtn) {
        atcBtn.classList.add(this.classes.loading);
        this._watchLoadingEnd(atcSubmitBtn, atcBtn);
      }

      atcSubmitBtn.click();
    }

    /**
     * Opens a MutationObserver on the main form submit button's `class` attribute.
     * When wlsn-product-form.js removes the loading class from the main button
     * (after the cart request completes), the observer mirrors that removal to the
     * sticky button and disconnects itself.
     * @param {HTMLButtonElement} mainBtn   - The main form submit button to observe.
     * @param {HTMLButtonElement} stickyBtn - The sticky ATC button whose loading class to clear.
     * @returns {void}
     */
    _watchLoadingEnd(mainBtn, stickyBtn) {
      // Disconnect any previous observer
      this._loadingObserver?.disconnect();

      this._loadingObserver = new MutationObserver(() => {
        if (!mainBtn.classList.contains(this.classes.loading)) {
          stickyBtn.classList.remove(this.classes.loading);
          this._loadingObserver.disconnect();
        }
      });

      this._loadingObserver.observe(mainBtn, { attributes: true, attributeFilter: ['class'] });
    }

    /* ── Display sync ── */

    /**
     * Reads the current variant ID and purchase type from the form inputs and
     * triggers a full display sync. Called once on init and whenever the form state
     * may not yet have fired change events (e.g. page load).
     * @returns {void}
     */
    _syncFromCurrentState() {
      const { variantInput, purchaseTypeInput } = this.elements;
      const purchaseType = purchaseTypeInput?.value || this._stickyData?.initPurchaseType || 'subscription';
      this._syncDisplay(variantInput?.value, purchaseType);
    }

    /**
     * Updates all price elements in the sticky bar (both mobile and desktop copies)
     * using the pre-formatted data from `_stickyData`, then refreshes the meta text.
     * @param {string} variantId    - The currently selected variant ID.
     * @param {string} purchaseType - 'subscription' or 'one-time'.
     * @returns {void}
     */
    _syncDisplay(variantId, purchaseType) {
      if (!this._stickyData) return;

      const variantData = this._stickyData.variants.find(
        (v) => String(v.variantId) === String(variantId)
      );
      const priceData = variantData?.[purchaseType === 'subscription' ? 'sub' : 'otp'];

      if (priceData) {
        this.querySelectorAll('.js-sticky-price').forEach((el) => {
          el.textContent = priceData.price;
        });

        this.querySelectorAll('.js-sticky-compare').forEach((el) => {
          el.textContent = priceData.comparePrice;
          el.hidden = !priceData.hasCompare;
        });

        this.querySelectorAll('.js-sticky-save').forEach((el) => {
          el.hidden = priceData.savings === null;
          if (priceData.savings !== null) {
            el.textContent = `SAVE ${priceData.savings}`;
          }
        });
      }

      this._syncMeta(purchaseType);
    }

    /**
     * Updates the sticky meta text line by reading the active quantity pill title,
     * the active frequency card label, and (for subscriptions) the selling plan name
     * directly from the buybox DOM. Parts are joined with ' · '.
     * @param {string} purchaseType - 'subscription' or 'one-time'.
     * @returns {void}
     */
    _syncMeta(purchaseType) {
      const metaEl = this.querySelector('.js-sticky-meta');
      if (!metaEl) return;

      const buybox = this.elements.buybox;
      if (!buybox) return;

      // Qty title from the active quantity pill
      const qtyTitle =
        buybox
          .querySelector('.js-quantity-pill.active .js-quantity-pill-title')
          ?.textContent?.trim() || '';

      // Purchase type label from the active frequency card
      const purchaseLabel =
        buybox
          .querySelector('.js-frequency-card.active .js-frequency-card-title')
          ?.textContent?.trim() || '';

      // Plan name from the visible group's subscribe card (subscription only)
      let planName = '';
      if (purchaseType === 'subscription') {
        const activeGroup = buybox.querySelector('.js-frequency-variant-group:not([hidden])');
        planName =
          activeGroup
            ?.querySelector('.js-frequency-card-subscribe .js-frequency-card-plan-name')
            ?.textContent?.trim() || '';
      }

      metaEl.textContent = [qtyTitle, purchaseLabel, planName].filter(Boolean).join(' · ');
    }

    /* ── Visibility (show / hide) ── */

    /**
     * Resolves every DOM element matching each hide condition and stores it in
     * `elementsCache` so all buyboxes on the page are accounted for.
     * @returns {void}
     */
    _setupHideConditions() {
      this.hideConditions.forEach((condition, index) => {
        const elements = Array.from(document.querySelectorAll(condition.selector));
        if (elements.length) {
          this.elementsCache[`condition${index}`] = elements;
          elements.forEach((_element, elementIndex) => {
            this.visibilityStates[`condition${index}-${elementIndex}`] = Boolean(condition.hideUntilPassed);
          });
        } else {
          console.warn(
            `[WLSNStickyBuybox] Could not find element for ${condition.description} (${condition.selector})`
          );
        }
      });
    }

    /**
     * Creates an IntersectionObserver for every hide-condition element. Buyboxes
     * keep the sticky hidden until they have fully passed above the viewport;
     * the footer hides it as soon as it enters the viewport.
     * @returns {void}
     */
    _setupIntersectionObservers() {
      this.hideConditions.forEach((condition, index) => {
        const elements = this.elementsCache[`condition${index}`] || [];

        elements.forEach((element, elementIndex) => {
          const stateKey = `condition${index}-${elementIndex}`;
          const observer = new IntersectionObserver(
            ([entry]) => {
              this.visibilityStates[stateKey] = condition.hideUntilPassed
                ? entry.boundingClientRect.bottom > 0
                : entry.isIntersecting;
              this._updateStickyState();
            },
            { threshold: 0 }
          );

          observer.observe(element);
          this.observers[stateKey] = observer;
        });
      });
    }

    /**
     * Fired on window resize. Re-evaluates the sticky visibility state.
     * @returns {void}
     */
    _handleResize() {
      this._updateStickyState();
    }

    /**
     * Shows or hides the sticky bar based on whether any hide-condition element
     * is currently visible in the viewport.
     * @returns {void}
     */
    _updateStickyState() {
      const anyVisible = Object.values(this.visibilityStates).some((s) => s === true);
      if (!anyVisible) {
        this._showSticky();
      } else {
        this._hideSticky();
      }
    }

    /**
     * Makes the sticky bar visible by adding the active class (triggers CSS transition).
     * No-ops if already active.
     * @returns {void}
     */
    _showSticky() {
      if (!this.classList.contains(this.classes.active)) {
        this.classList.add(this.classes.active);
      }
    }

    /**
     * Hides the sticky bar by removing the active class (triggers CSS transition).
     * No-ops if already hidden.
     * @returns {void}
     */
    _hideSticky() {
      if (this.classList.contains(this.classes.active)) {
        this.classList.remove(this.classes.active);
      }
    }

    /* ── Cleanup ── */

    /**
     * Disconnects all IntersectionObservers and the loading MutationObserver,
     * removes all event listeners, and clears the elements cache.
     * Called automatically when the element is removed from the DOM.
     * @returns {void}
     */
    _cleanup() {
      Object.values(this.observers).forEach((o) => o?.disconnect());
      this._loadingObserver?.disconnect();

      const { variantInput, purchaseTypeInput, modifyBtn, atcBtn } = this.elements;
      if (variantInput) variantInput.removeEventListener('change', this.boundHandleVariantChange);
      if (purchaseTypeInput) purchaseTypeInput.removeEventListener('change', this.boundHandlePurchaseTypeChange);
      if (modifyBtn) modifyBtn.removeEventListener('click', this.boundHandleModify);
      if (atcBtn) atcBtn.removeEventListener('click', this.boundHandleAtc);

      window.removeEventListener('resize', this.boundHandleResize);
      this.elementsCache = null;
    }
  }

  customElements.define('wlsn-buybox-sticky', WLSNStickyBuybox);
}
